import { apiUrl } from "config/envConfig";
import http from "./httpService";
import { DEFAULT_PAGE, DEFAULT_ROWS_PER_PAGE } from "constants/paginationConstants";
import { RegistrationStatusEnum } from "enums/shared";

const path = "/user-mgmt/admin";
const url = apiUrl;

/**
 * Fetch customer requests with pagination and filtering
 *
 * @param {number} pageSize   Number of records per page.
 * @param {number} pageNumber Current page number.
 * @param {string} searchBar  Search text.
 * @param {number} status     Status filter.
 *
 * @return {Promise<Object>} Paginated customer data.
 */
export const getCustomerRequests = async (
  pageSize = DEFAULT_ROWS_PER_PAGE,
  pageNumber = DEFAULT_PAGE,
  FullName = "",
  status = [RegistrationStatusEnum.pendingForApproval], //default status to 3
  PhoneNumber = "",
  Email = "",
  descendingNameSort = null,
  descendingEmailSort = null
) => {
  // Ensure we send a 1-based page number to match server validation.
  const params = new URLSearchParams({ pageSize, pageNumber: Math.max(1, pageNumber) });

  if (Array.isArray(status) && status.length > 0) {
    status.forEach((statusValue) => params.append("Status", statusValue));
  }

  if (FullName) params.append("FullName", FullName);
  if (PhoneNumber) params.append("PhoneNumber", PhoneNumber);
  if (Email) params.append("Email", Email);
  if (descendingNameSort !== null)
    params.append("DescendingNameSort", descendingNameSort);
  if (descendingEmailSort !== null)
    params.append("DescendingEmailSort", descendingEmailSort);

  const {
    data: { data },
  } = await http.get(`${url}${path}/senders?${params.toString()}`);
  // } = await http.get(`${url}${path}/senders${query}`);

  return {
    page: data.page || [],
    totalRecords: data.totalRecords || 0,
  };
};

export const approveSender = async (senderId) => {
  const {
    data: { data },
  } = await http.patch(`${url}${path}/sender/approve?senderId=${senderId}`);
  return data;
};

//data to SenderOrdersTab
export const getSenderInfo = async (senderId) => {
  const {
    data: { data },
  } = await http.get(`${url}${path}/sender?senderId=${senderId}`);
  return data;
};

/**
 * Fetch a file by its ID.
 *
 * @param {string} fileId The ID of the file to be fetched.
 * @returns {Promise<Blob>} The file data as a Blob.
 */
export const getFile = async (fileId) => {
  const response = await http.get(`${url}${path}/file?fileId=${fileId}`, {
    responseType: "blob",
  });
  return response.data;
};

/**
 * Fetch sender's contacts by senderId
 *
 * @param {string} senderId
 * @returns {Promise<Object>} { page: Array, totalRecords: number }
 */
export const getSenderContacts = async (senderId) => {
  const { data } = await http.get(
    `${url}${path}/SenderContacts?senderId=${senderId}`
  );
  return data;
};

/**
 * Toggle acceptZero status for a sender
 *
 * @param {string} senderId The ID of the sender
 * @param {boolean} acceptZero The new acceptZero status
 * @param {string | null} zeroStartDate Start date (ISO date string, e.g., "2025-11-16")
 * @param {string | null} zeroEndDate End date (ISO date string, e.g., "2025-11-30")
 * @returns {Promise<Object>} Response data
 */
export const toggleAcceptZero = async (senderId, acceptZero, zeroStartDate = null, zeroEndDate = null) => {
  // Convert dates to ISO 8601 UTC format (PostgreSQL requires UTC for timestamp with time zone)
  const body = {
    acceptZero,
    zeroStartDate: zeroStartDate ? new Date(`${zeroStartDate}T00:00:00Z`).toISOString() : null,
    zeroEndDate: zeroEndDate ? new Date(`${zeroEndDate}T23:59:59Z`).toISOString() : null,
  };

  const { data } = await http.put(
    `${url}${path}/SenderAcceptZero/${senderId}`,
    body
  );
  return data;
};

/**
 * Fetch all senders (approved)
 *
 * @returns {Promise<Array>} Array of senders
 */
export const getAllSenders = async () => {
  // Some backends enforce limits on pageSize and return a 400 with
  // "InvalidModelData" when the requested size is too large. Try a
  // small set of descending sizes and gracefully return an empty
  // array if all attempts fail.
  const sizesToTry = [1000, 500, 100];
  for (const size of sizesToTry) {
    try {
      // Use 1-based pageNumber to satisfy server-side model validation
      const {
        data: { data },
      } = await http.get(`${url}${path}/senders?pageSize=${size}&pageNumber=1`);
      console.log(`Senders fetched (pageSize=${size}):`, data.page);
      return data.page || [];
    } catch (error) {
      const respMsg = error?.response?.data?.message || error?.message || "";
      // If server complains about invalid model data, try a smaller pageSize.
      // If server complains about invalid model data, try a smaller pageSize.
      if (typeof respMsg === "string" && respMsg.toLowerCase().includes("invalidmodeldata")) {
        console.warn(`senders fetch failed for pageSize=${size} (pageNumber=0): ${respMsg} — trying smaller size`);
        continue;
      }

      // For other errors, log and break out (likely an auth/connection issue)
      console.error(`Error fetching senders (pageSize=${size}):`, error);
      break;
    }
  }

  console.error("All senders fetch attempts failed, returning an empty array.");
  return [];
};
